import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  PreconditionExpr,
  PreconditionFieldDef,
} from "@/services/platformEnterprisesService";

interface Props {
  expr: PreconditionExpr;
  fields: PreconditionFieldDef[];
  onChange: (next: PreconditionExpr) => void;
  /** If true, only numeric expression kinds are allowed. */
  numericOnly?: boolean;
  /** Optional placeholder for the constant input. */
  constPlaceholder?: string;
  /** When set, render a select dropdown instead of a free-text const input. */
  selectOptions?: Array<{ value: string; label: string }>;
}

type ExprKind = PreconditionExpr["kind"];

const NUMERIC_KINDS: ExprKind[] = ["field", "const", "sum", "percentageOf"];
const ALL_KINDS: ExprKind[] = ["field", "const", "sum", "product", "percentageOf"];

function defaultFor(kind: ExprKind): PreconditionExpr {
  switch (kind) {
    case "field":
      return { kind: "field", key: "" };
    case "const":
      return { kind: "const", value: "" };
    case "sum":
      return { kind: "sum", fields: [] };
    case "product":
      return { kind: "product", factors: [] };
    case "percentageOf":
      return { kind: "percentageOf", percentage: 0, field: "" };
  }
}

export function PreconditionExprInput({
  expr,
  fields,
  onChange,
  numericOnly = false,
  constPlaceholder,
  selectOptions,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const usableFields = fields.filter((f) => f.key.trim() !== "");
  const numericFields = usableFields.filter((f) => f.type === "number");
  const allowedKinds = numericOnly ? NUMERIC_KINDS : ALL_KINDS;

  function setKind(kind: ExprKind): void {
    if (kind === expr.kind) return;
    onChange(defaultFor(kind));
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={expr.kind} onValueChange={(v) => setKind(v as ExprKind)}>
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allowedKinds.map((k) => (
            <SelectItem key={k} value={k}>
              {t(`enterpriseProductBuilder.exprKind.${k}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {expr.kind === "field" && (
        <Select
          value={expr.key || undefined}
          onValueChange={(v) => onChange({ kind: "field", key: v })}
        >
          <SelectTrigger className="h-8 min-w-[140px] text-xs">
            <SelectValue placeholder={t("enterpriseProductBuilder.selectField")} />
          </SelectTrigger>
          <SelectContent>
            {(numericOnly ? numericFields : usableFields).map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.label || f.key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {expr.kind === "const" && selectOptions && selectOptions.length > 0 ? (
        <Select
          value={String(expr.value ?? "")}
          onValueChange={(v) => onChange({ kind: "const", value: v })}
        >
          <SelectTrigger className="h-8 w-[180px] text-xs">
            <SelectValue placeholder={constPlaceholder ?? t("enterpriseProductBuilder.valueLabel")} />
          </SelectTrigger>
          <SelectContent>
            {selectOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : expr.kind === "const" ? (
        <Input
          value={String(expr.value ?? "")}
          onChange={(e) =>
            onChange({
              kind: "const",
              value: numericOnly ? Number(e.target.value) : e.target.value,
            })
          }
          type={numericOnly ? "number" : "text"}
          placeholder={constPlaceholder ?? t("enterpriseProductBuilder.valueLabel")}
          className="h-8 w-[140px] text-xs"
        />
      ) : null}

      {expr.kind === "sum" && (
        <div className="flex flex-wrap gap-1">
          {numericFields.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              {t("enterpriseProductBuilder.addNumberFieldsFirst")}
            </span>
          ) : (
            numericFields.map((f) => {
              const checked = expr.fields.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() =>
                    onChange({
                      kind: "sum",
                      fields: checked
                        ? expr.fields.filter((k) => k !== f.key)
                        : [...expr.fields, f.key],
                    })
                  }
                  className={
                    "rounded-md border px-2 py-0.5 text-xs transition-colors " +
                    (checked
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input bg-background")
                  }
                >
                  {f.label || f.key}
                </button>
              );
            })
          )}
        </div>
      )}

      {expr.kind === "percentageOf" && (
        <>
          <Input
            type="number"
            min={0}
            max={100}
            value={expr.percentage}
            onChange={(e) =>
              onChange({
                ...expr,
                percentage: Number(e.target.value),
              })
            }
            className="h-8 w-[80px] text-xs"
            placeholder="%"
          />
          <span className="text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.ofField")}
          </span>
          <Select
            value={expr.field || undefined}
            onValueChange={(v) => onChange({ ...expr, field: v })}
          >
            <SelectTrigger className="h-8 min-w-[140px] text-xs">
              <SelectValue placeholder={t("enterpriseProductBuilder.selectField")} />
            </SelectTrigger>
            <SelectContent>
              {numericFields.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {expr.kind === "product" && (
        <span className="text-xs text-muted-foreground">
          {t("enterpriseProductBuilder.exprProductHint")}
        </span>
      )}
    </div>
  );
}
